'use client';

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import useAuthStore from '@/store/auth';
import { useMutationEditAccountCredential } from '@/api/queries/customers';
import toast from 'react-hot-toast';
import { EditAccountCredentialForm } from '@/types/form';
import { Loader2, Eye, EyeOff } from 'lucide-react';

const schema = Yup.object().shape({
  email: Yup.string().optional(),
  current_password: Yup.string().required('Current Password is required'),
  new_password: Yup.string()
    .min(8, 'New password must be at least 8 characters')
    .required('New Password is required'),
  re_password: Yup.string()
    .required('Please retype your new password')
    .test('passwords-match', 'Passwords do not match', function (value) {
      return this.parent.new_password === value;
    }),
});

export default function CredentialsTab() {
  const user = useAuthStore((state) => state.user);
  const { mutateAsync, isPending } = useMutationEditAccountCredential();

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditAccountCredentialForm>({
    // @ts-ignore
    resolver: yupResolver(schema),
    defaultValues: {
      email: user?.email || '',
      current_password: '',
      new_password: '',
      re_password: '',
    },
  });

  useEffect(() => {
    if (user?.email) {
      reset({
        email: user.email,
        current_password: '',
        new_password: '',
        re_password: '',
      });
    }
  }, [user?.email, reset]);

  const onSubmit = async (data: EditAccountCredentialForm) => {
    try {
      await mutateAsync({
        params: {
          email: user?.email || data.email,
          c_password: data.current_password,
          password: data.new_password,
        },
      });
      reset({
        email: user?.email || '',
        current_password: '',
        new_password: '',
        re_password: '',
      });
      toast.success('Credentials updated successfully!');
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Could not update credentials';
      toast.error(`Update failed: ${message}`);
    }
  };

  const onValidationError = (formErrors: any) => {
    const errorKeys = Object.keys(formErrors);
    if (errorKeys.length > 0) {
      const firstError = formErrors[errorKeys[0]];
      const msg = typeof firstError?.message === 'string' ? firstError.message : 'Please check required fields';
      toast.error(msg);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onValidationError)} className="p-5 md:p-0 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200">
              Email Address
            </label>
            <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-gray-800 px-2 py-0.5 rounded-md">
              Cannot be changed
            </span>
          </div>
          <input
            type="email"
            value={user?.email || ''}
            disabled
            readOnly
            className="w-full px-4 py-3 rounded-xl text-sm border border-slate-200 dark:border-gray-700/60 bg-slate-100/80 dark:bg-gray-800/60 text-slate-500 dark:text-slate-400 cursor-not-allowed select-none transition-all"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
            Current Password <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              {...register('current_password')}
              type={showCurrent ? 'text' : 'password'}
              placeholder="Enter current password"
              className="w-full px-4 py-3 pr-11 rounded-xl text-sm border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
            />
            <button
              type="button"
              onClick={() => setShowCurrent(!showCurrent)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1"
            >
              {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.current_password && (
            <p className="text-xs text-red-500 mt-1">{errors.current_password.message}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
            New Password <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              {...register('new_password')}
              type={showNew ? 'text' : 'password'}
              placeholder="Enter new password (min 8 chars)"
              className="w-full px-4 py-3 pr-11 rounded-xl text-sm border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
            />
            <button
              type="button"
              onClick={() => setShowNew(!showNew)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1"
            >
              {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.new_password && (
            <p className="text-xs text-red-500 mt-1">{errors.new_password.message}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
            Retype Password <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              {...register('re_password')}
              type={showConfirm ? 'text' : 'password'}
              placeholder="Retype new password"
              className="w-full px-4 py-3 pr-11 rounded-xl text-sm border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1"
            >
              {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.re_password && (
            <p className="text-xs text-red-500 mt-1">{errors.re_password.message}</p>
          )}
        </div>
      </div>

      <div className="pt-3 pb-28 md:pb-2 flex justify-end">
        <button
          type="submit"
          disabled={isPending}
          className="w-full md:w-auto md:min-w-[170px] py-3 px-6 rounded-xl font-semibold text-sm text-white bg-red-600 hover:bg-red-700 active:scale-[0.99] disabled:opacity-60 transition-all flex items-center justify-center gap-2 shadow-sm"
        >
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Change Password'
          )}
        </button>
      </div>
    </form>
  );
}
